import { useFrame, useThree } from '@react-three/fiber'
import { observer } from 'mobx-react-lite'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useRunState } from '../../provider/useRunState'
import {
    level4StarsCenterX,
    level4StarsCenterY,
    level4StarsCenterZ,
    level4StarsDepthFar,
    level4StarsDepthNear,
    level4StarsHeightMax,
    level4StarsHeightMin,
    level4StarsHorizontalSpread,
    level4StarsSizeMax,
    level4StarsSizeMin,
    level4StarsTwinkleMax,
    level4StarsTwinkleMin,
} from '../../config/constants'

const STAR_COUNT = 420

function randomFromSeed(seed: number) {
    return Math.abs(Math.sin(seed * 12.9898 + 78.233) * 43758.5453) % 1
}

export const Level4Stars = observer(function Level4Stars() {
    const runState = useRunState()
    const { camera } = useThree()
    const meshRef = useRef<THREE.InstancedMesh>(null)
    const materialRef = useRef<THREE.ShaderMaterial>(null)
    const matrixRef = useRef(new THREE.Matrix4())
    const scaleRef = useRef(new THREE.Vector3(1, 1, 1))
    const cameraQuaternionRef = useRef(new THREE.Quaternion())

    const baseGeometry = useMemo(() => new THREE.PlaneGeometry(1, 1), [])

    const starData = useMemo(() => {
        const center = new THREE.Vector3(
            level4StarsCenterX,
            level4StarsCenterY,
            level4StarsCenterZ,
        )
        const horizontalSpread = Math.max(level4StarsHorizontalSpread, 0)
        const halfHorizontalSpread = horizontalSpread * 0.5
        const heightMin = Math.min(level4StarsHeightMin, level4StarsHeightMax)
        const heightMax = Math.max(level4StarsHeightMin, level4StarsHeightMax)
        const depthNear = Math.max(level4StarsDepthNear, level4StarsDepthFar)
        const depthFar = Math.min(level4StarsDepthNear, level4StarsDepthFar)
        const sizeMin = Math.min(level4StarsSizeMin, level4StarsSizeMax)
        const sizeMax = Math.max(level4StarsSizeMin, level4StarsSizeMax)
        const twinkleMin = Math.min(level4StarsTwinkleMin, level4StarsTwinkleMax)
        const twinkleMax = Math.max(level4StarsTwinkleMin, level4StarsTwinkleMax)

        const data = Array.from({ length: STAR_COUNT }, (_, index) => {
            const horizontal = (randomFromSeed(index + 0.31) - 0.5) * horizontalSpread
            const depth = THREE.MathUtils.lerp(
                depthNear,
                depthFar,
                randomFromSeed(index + 1.93),
            )
            const y = THREE.MathUtils.lerp(
                heightMin,
                heightMax,
                randomFromSeed(index + 4.71),
            )

            return {
                position: new THREE.Vector3(
                    center.x + horizontal,
                    center.y + y,
                    center.z + depth,
                ),
                size: THREE.MathUtils.lerp(
                    sizeMin,
                    sizeMax,
                    randomFromSeed(index + 7.12),
                ),
                phase: randomFromSeed(index + 9.81) * Math.PI * 2,
                twinkle: THREE.MathUtils.lerp(
                    twinkleMin,
                    twinkleMax,
                    randomFromSeed(index + 13.57),
                ),
            }
        })

        return data
    }, [])

    const material = useMemo(() => new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: true,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
        uniforms: {
            uTime: { value: 0 },
        },
        vertexShader: `
            attribute float aPhase;
            attribute float aSize;
            attribute float aTwinkle;

            uniform float uTime;

            varying float vTwinkle;
            varying vec2 vUv;

            void main() {
                float twinkle = 0.7 + 0.3 * sin(uTime * aTwinkle + aPhase);
                vTwinkle = twinkle;
                vUv = uv;

                vec3 transformed = position * (aSize * twinkle);
                vec4 modelViewPosition = modelViewMatrix * instanceMatrix * vec4(transformed, 1.0);
                gl_Position = projectionMatrix * modelViewPosition;
            }
        `,
        fragmentShader: `
            varying float vTwinkle;
            varying vec2 vUv;

            void main() {
                vec2 p = abs((vUv - 0.5) * 1.65);
                float starShape = max(p.x, p.y) + min(p.x, p.y) * 2.8;
                float core = 1.0 - smoothstep(0.02, 0.18, starShape);
                float glow = 1.0 - smoothstep(0.08, 0.42, starShape);
                float verticalRay =
                    exp(-p.x * 18.0) *
                    exp(-p.y * 2.7) * 0.75;
                float horizontalRay =
                    exp(-p.y * 18.0) *
                    exp(-p.x * 2.7) * 0.75;
                float alpha = core + glow * 0.18 + verticalRay + horizontalRay;
                alpha *= 0.9 + vTwinkle * 0.35;
                alpha *= 1.0 - smoothstep(0.68, 0.95, max(p.x, p.y));

                if (alpha <= 0.01) {
                    discard;
                }

                gl_FragColor = vec4(vec3(1.0), alpha);
            }
        `,
    }), [])

    const geometry = useMemo(() => {
        const geometry = baseGeometry.clone()
        const phases = new Float32Array(STAR_COUNT)
        const sizes = new Float32Array(STAR_COUNT)
        const twinkles = new Float32Array(STAR_COUNT)

        for (let index = 0; index < STAR_COUNT; index += 1) {
            phases[index] = starData[index].phase
            sizes[index] = starData[index].size
            twinkles[index] = starData[index].twinkle
        }

        geometry.setAttribute('aPhase', new THREE.InstancedBufferAttribute(phases, 1))
        geometry.setAttribute('aSize', new THREE.InstancedBufferAttribute(sizes, 1))
        geometry.setAttribute('aTwinkle', new THREE.InstancedBufferAttribute(twinkles, 1))
        return geometry
    }, [baseGeometry, starData])

    useFrame(({ clock }) => {
        if (!meshRef.current) {
            return
        }

        material.uniforms.uTime.value = clock.elapsedTime

        const cameraQuaternion = cameraQuaternionRef.current.copy(camera.quaternion)
        const matrix = matrixRef.current
        const scale = scaleRef.current

        for (let index = 0; index < starData.length; index += 1) {
            const star = starData[index]
            matrix.compose(star.position, cameraQuaternion, scale)
            meshRef.current.setMatrixAt(index, matrix)
        }

        meshRef.current.instanceMatrix.needsUpdate = true
    })

    return (
        <instancedMesh
            ref={meshRef}
            args={[geometry, material, STAR_COUNT]}
            frustumCulled={false}
            renderOrder={5}
            visible={runState.trackRegionState.activeRegion === 4}
        />
    )
})
